import React, { useState, useEffect } from "react";
import axios from "axios";

const ReviewsDisplay = ({ type, id, showResponseUI = false }) => {
  // type: 'shop' or 'professional'
  // id: shopId or professionalId
  // showResponseUI: true for business owners to add responses

  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ratingBreakdown, setRatingBreakdown] = useState([]);

  const [filter, setFilter] = useState("recent"); // recent, highest, lowest, helpful
  const [ratingFilter, setRatingFilter] = useState(null); // null or 1-5
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [responseText, setResponseText] = useState({});
  const [respondingTo, setRespondingTo] = useState(null);

  const isMobile = window.innerWidth <= 768;

  useEffect(() => {
    fetchReviews();
  }, [id, filter, ratingFilter, page]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const endpoint =
        type === "shop"
          ? `/api/reviews/shop/${id}`
          : `/api/reviews/professional/${id}`;

      const params = {
        sortBy: filter,
        page,
        limit: 10,
      };

      if (ratingFilter) {
        params.rating = ratingFilter;
      }

      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}${endpoint}`,
        { params },
      );

      setReviews(response.data.reviews || []);
      setTotalPages(response.data.totalPages || 1);
      setRatingBreakdown(response.data.ratingBreakdown || []);
      setLoading(false);
    } catch (err) {
      setError("Failed to load reviews");
      setLoading(false);
    }
  };

  const handleAddResponse = async (reviewId) => {
    if (!responseText[reviewId] || responseText[reviewId].trim().length === 0) {
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/reviews/${reviewId}/response`,
        { responseText: responseText[reviewId] },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setResponseText({ ...responseText, [reviewId]: "" });
      setRespondingTo(null);
      fetchReviews();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to add response");
    }
  };

  const renderStars = (rating) => {
    return (
      <div style={{ display: "flex", gap: "2px" }}>
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            style={{
              fontSize: isMobile ? "12px" : "16px",
              color: star <= rating ? "#fbbf24" : "#d1d5db",
            }}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const getRatingStats = () => {
    const total = ratingBreakdown.reduce((sum, item) => sum + item.count, 0);
    if (total === 0) return { average: 0, total: 0 };

    const sum = ratingBreakdown.reduce(
      (acc, item) => acc + item._id * item.count,
      0,
    );
    const average = (sum / total).toFixed(1);

    return { average, total };
  };

  const { average, total } = getRatingStats();

  if (loading && reviews.length === 0) {
    return (
      <div
        style={{
          padding: isMobile ? "16px" : "24px",
          textAlign: "center",
          fontSize: isMobile ? "10px" : "12px",
          color: "#6b7280",
        }}
      >
        Loading reviews...
      </div>
    );
  }

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: "linear-gradient(135deg, #1a659e 0%, #2980b9 100%)",
          padding: isMobile ? "16px" : "20px 24px",
          color: "#ffffff",
        }}
      >
        <h2
          style={{
            margin: "0 0 4px 0",
            fontSize: isMobile ? "12px" : "18px",
            fontWeight: "600",
          }}
        >
          Customer Reviews
        </h2>
        <p
          style={{
            margin: 0,
            fontSize: isMobile ? "10px" : "13px",
            opacity: 0.9,
          }}
        >
          {total} {total === 1 ? "review" : "reviews"}
        </p>
      </div>

      {/* Rating Summary */}
      {total > 0 && (
        <div
          style={{
            padding: isMobile ? "16px" : "20px 24px",
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? "16px" : "24px",
            alignItems: isMobile ? "center" : "flex-start",
          }}
        >
          {/* Average */}
          <div
            style={{
              textAlign: "center",
              minWidth: isMobile ? "auto" : "120px",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "12px" : "40px",
                fontWeight: "700",
                color: "#111827",
                marginBottom: "4px",
              }}
            >
              {average}
            </div>
            <div style={{ marginBottom: "8px" }}>
              {renderStars(Math.round(parseFloat(average)))}
            </div>
            <div
              style={{
                fontSize: isMobile ? "9px" : "11px",
                color: "#6b7280",
              }}
            >
              Based on {total} {total === 1 ? "review" : "reviews"}
            </div>
          </div>

          {/* Breakdown */}
          <div style={{ flex: 1, width: "100%" }}>
            {[5, 4, 3, 2, 1].map((rating) => {
              const count =
                ratingBreakdown.find((r) => r._id === rating)?.count || 0;
              const percentage = total > 0 ? (count / total) * 100 : 0;

              return (
                <div
                  key={rating}
                  onClick={() =>
                    setRatingFilter(ratingFilter === rating ? null : rating)
                  }
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: isMobile ? "6px" : "8px",
                    marginBottom: "6px",
                    cursor: "pointer",
                    opacity: ratingFilter && ratingFilter !== rating ? 0.5 : 1,
                  }}
                >
                  <span
                    style={{
                      fontSize: isMobile ? "9px" : "11px",
                      color: "#6b7280",
                      minWidth: "40px",
                    }}
                  >
                    {rating} star
                  </span>
                  <div
                    style={{
                      flex: 1,
                      height: isMobile ? "6px" : "8px",
                      background: "#e5e7eb",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        width: `${percentage}%`,
                        height: "100%",
                        background: "#fbbf24",
                        transition: "width 0.3s",
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: isMobile ? "9px" : "11px",
                      color: "#6b7280",
                      minWidth: "40px",
                      textAlign: "right",
                    }}
                  >
                    {count}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          padding: isMobile ? "12px 16px" : "16px 24px",
          borderBottom: "1px solid #e5e7eb",
          display: "flex",
          gap: isMobile ? "6px" : "8px",
          flexWrap: "wrap",
        }}
      >
        {["recent", "highest", "lowest", "helpful"].map((filterType) => (
          <button
            key={filterType}
            onClick={() => setFilter(filterType)}
            style={{
              padding: isMobile ? "6px 10px" : "8px 12px",
              fontSize: isMobile ? "9px" : "11px",
              fontWeight: "500",
              border: filter === filterType ? "none" : "1px solid #d1d5db",
              background: filter === filterType ? "#2980b9" : "#ffffff",
              color: filter === filterType ? "#ffffff" : "#4b5563",
              borderRadius: "6px",
              cursor: "pointer",
              textTransform: "capitalize",
            }}
          >
            {filterType}
          </button>
        ))}
        {ratingFilter && (
          <button
            onClick={() => setRatingFilter(null)}
            style={{
              padding: isMobile ? "6px 10px" : "8px 12px",
              fontSize: isMobile ? "9px" : "11px",
              fontWeight: "500",
              border: "1px solid #d1d5db",
              background: "#fef3c7",
              color: "#92400e",
              borderRadius: "6px",
              cursor: "pointer",
            }}
          >
            {ratingFilter} ⭐ ✕
          </button>
        )}
      </div>

      {/* Reviews List */}
      <div>
        {reviews.length === 0 ? (
          <div
            style={{
              padding: isMobile ? "32px 16px" : "40px 24px",
              textAlign: "center",
              color: "#6b7280",
            }}
          >
            <div
              style={{
                fontSize: isMobile ? "12px" : "48px",
                marginBottom: "12px",
              }}
            >
              📝
            </div>
            <p
              style={{
                margin: "0 0 8px 0",
                fontSize: isMobile ? "11px" : "14px",
                fontWeight: "600",
              }}
            >
              No reviews yet
            </p>
            <p
              style={{
                margin: 0,
                fontSize: isMobile ? "10px" : "12px",
              }}
            >
              Be the first to share your experience!
            </p>
          </div>
        ) : (
          reviews.map((review) => (
            <div
              key={review._id}
              style={{
                padding: isMobile ? "16px" : "20px 24px",
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              {/* Review Header */}
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  marginBottom: "12px",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: isMobile ? "11px" : "13px",
                      fontWeight: "600",
                      color: "#111827",
                      marginBottom: "4px",
                    }}
                  >
                    {review.customerName ||
                      review.customerId?.firstName ||
                      review.bookingId?.customerName?.split(" ")[0] ||
                      "Anonymous"}
                  </div>
                  {renderStars(review.rating)}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "9px" : "11px",
                    color: "#9ca3af",
                  }}
                >
                  {new Date(review.createdAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </div>
              </div>

              {/* Review Text */}
              <p
                style={{
                  margin: "0 0 12px 0",
                  fontSize: isMobile ? "10px" : "12px",
                  color: "#4b5563",
                  lineHeight: 1.6,
                }}
              >
                {review.reviewText}
              </p>

              {/* Service Ratings */}
              {review.serviceRatings &&
                Object.keys(review.serviceRatings).some(
                  (k) => review.serviceRatings[k] > 0,
                ) && (
                  <div
                    style={{
                      display: "flex",
                      gap: isMobile ? "8px" : "12px",
                      flexWrap: "wrap",
                      marginBottom: "12px",
                    }}
                  >
                    {Object.entries(review.serviceRatings).map(
                      ([key, value]) =>
                        value > 0 && (
                          <div
                            key={key}
                            style={{
                              fontSize: isMobile ? "9px" : "10px",
                              color: "#6b7280",
                              background: "#f3f4f6",
                              padding: isMobile ? "4px 8px" : "4px 10px",
                              borderRadius: "4px",
                            }}
                          >
                            {key}: {value}★
                          </div>
                        ),
                    )}
                  </div>
                )}

              {/* Business Response */}
              {review.businessResponse?.text && (
                <div
                  style={{
                    marginTop: "12px",
                    padding: isMobile ? "10px" : "12px",
                    background: "#f0f9ff",
                    borderLeft: "3px solid #2980b9",
                    borderRadius: "6px",
                  }}
                >
                  <div
                    style={{
                      fontSize: isMobile ? "9px" : "10px",
                      fontWeight: "600",
                      color: "#1e40af",
                      marginBottom: "6px",
                    }}
                  >
                    Response from Business
                  </div>
                  <p
                    style={{
                      margin: 0,
                      fontSize: isMobile ? "10px" : "11px",
                      color: "#1e3a8a",
                      lineHeight: 1.5,
                    }}
                  >
                    {review.businessResponse.text}
                  </p>
                  <div
                    style={{
                      marginTop: "6px",
                      fontSize: isMobile ? "8px" : "9px",
                      color: "#64748b",
                    }}
                  >
                    {new Date(
                      review.businessResponse.respondedAt,
                    ).toLocaleDateString()}
                  </div>
                </div>
              )}

              {/* Add Response UI (for business owners) */}
              {showResponseUI && !review.businessResponse?.text && (
                <div style={{ marginTop: "12px" }}>
                  {respondingTo === review._id ? (
                    <div>
                      <textarea
                        value={responseText[review._id] || ""}
                        onChange={(e) =>
                          setResponseText({
                            ...responseText,
                            [review._id]: e.target.value,
                          })
                        }
                        placeholder="Write your response (max 500 characters)"
                        maxLength={500}
                        rows={3}
                        style={{
                          width: "100%",
                          padding: isMobile ? "8px" : "10px",
                          fontSize: isMobile ? "10px" : "12px",
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          fontFamily: "inherit",
                          marginBottom: "8px",
                        }}
                      />
                      <div style={{ display: "flex", gap: "8px" }}>
                        <button
                          onClick={() => handleAddResponse(review._id)}
                          style={{
                            padding: isMobile ? "6px 12px" : "8px 16px",
                            fontSize: isMobile ? "10px" : "12px",
                            background: "#2980b9",
                            color: "#ffffff",
                            border: "none",
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                        >
                          Submit
                        </button>
                        <button
                          onClick={() => {
                            setRespondingTo(null);
                            setResponseText({
                              ...responseText,
                              [review._id]: "",
                            });
                          }}
                          style={{
                            padding: isMobile ? "6px 12px" : "8px 16px",
                            fontSize: isMobile ? "10px" : "12px",
                            background: "#ffffff",
                            color: "#6b7280",
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            cursor: "pointer",
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setRespondingTo(review._id)}
                      style={{
                        padding: isMobile ? "6px 12px" : "8px 16px",
                        fontSize: isMobile ? "10px" : "12px",
                        background: "#ffffff",
                        color: "#2980b9",
                        border: "1px solid #2980b9",
                        borderRadius: "6px",
                        cursor: "pointer",
                      }}
                    >
                      Respond to Review
                    </button>
                  )}
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div
          style={{
            padding: isMobile ? "12px 16px" : "16px 24px",
            display: "flex",
            justifyContent: "center",
            gap: "8px",
          }}
        >
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            style={{
              padding: isMobile ? "6px 12px" : "8px 16px",
              fontSize: isMobile ? "10px" : "12px",
              background: page === 1 ? "#f3f4f6" : "#ffffff",
              color: page === 1 ? "#9ca3af" : "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
          >
            Previous
          </button>
          <div
            style={{
              padding: isMobile ? "6px 12px" : "8px 16px",
              fontSize: isMobile ? "10px" : "12px",
              color: "#6b7280",
            }}
          >
            Page {page} of {totalPages}
          </div>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            style={{
              padding: isMobile ? "6px 12px" : "8px 16px",
              fontSize: isMobile ? "10px" : "12px",
              background: page === totalPages ? "#f3f4f6" : "#ffffff",
              color: page === totalPages ? "#9ca3af" : "#374151",
              border: "1px solid #d1d5db",
              borderRadius: "6px",
              cursor: page === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default ReviewsDisplay;
